import React from 'react';
import mapboxgl from 'mapbox-gl';
import DrawControl, {DrawControlProps} from './DrawControl';
import SidebarControl, {SidebarControlProps} from './SidebarControl';
import {compact} from 'lodash';
import LoadingControl, {LoadingControlProps} from './LoadingControl';
import {MapboxControl} from '../../../utils/mapbox/Mapbox';
import FullscreenControl, {FullscreenControlProps} from './FullscreenControl';
import SearchBoxControl, {SearchBoxControlProps} from './SearchBoxControl';

interface GetControlsOptions extends
    DrawControlProps,
    SidebarControlProps,
    FullscreenControlProps
{
    loadingMessages: string[];
    showDrawControl: boolean;
    showGeolocateControl: boolean;
    showSearchBoxControl: boolean;
    showFullscreenControl: boolean;
    onSearchResultChange: SetState<SearchResult | undefined>;
    user: User | undefined;
}

export default function getControls({
    bounds,
    highlight,
    interaction,
    loadingMessages,
    map,
    onInteractionChange,
    onOverlaysChange,
    onPopupChange,
    onSearchResultChange,
    onSidebarChange,
    overlays,
    showDrawControl,
    showFullscreenControl,
    showGeolocateControl,
    showLayersControl,
    showPrintControl,
    showSearchBoxControl,
    sidebar,
    trackDispatcher,
    userLayers,
    waypointDispatcher,
    zoom,
    user,
}: GetControlsOptions) {

    if (sidebar.type === 'print') {
        return [[mapboxgl.ScaleControl, {
            unit: user ? user.unitSystem : 'imperial',
            maxWidth: 200
        }, 'bottom-left']] as MapboxControl<any>[];
    }

    const sidebarControlProps: SidebarControlProps = {
        onSidebarChange,
        interaction,
        showPrintControl,
        bounds,
        zoom,
        highlight,
        userLayers,
        sidebar,
        showLayersControl
    };

    const drawControlProps: DrawControlProps = {
        interaction,
        onInteractionChange,
        onPopupChange,
        waypointDispatcher,
        trackDispatcher,
        map,
        overlays,
        onOverlaysChange,
    };

    const fullscreenControlProps: FullscreenControlProps = {
        highlight,
        bounds,
        userLayers,
        zoom,
    };

    const searchBoxControlProps: SearchBoxControlProps = {
        onSearchResultChange,
        center: bounds && bounds.getCenter(),
        inputProps: {placeholder: 'Search / Coordinates'},
    };

    const loadingControlProps: LoadingControlProps = {
        isLoading: loadingMessages.length !== 0,
        title: 'Loading ' + loadingMessages.join(', '),
    };

    return compact([
        showSearchBoxControl && [SearchBoxControl, searchBoxControlProps, 'top-left'],

        [mapboxgl.NavigationControl, {
            showCompass: false
        }, 'top-left'],

        showGeolocateControl !== false && [mapboxgl.GeolocateControl, {
            positionOptions: {
                enableHighAccuracy: true
            },
            showUserLocation: false
        }, 'top-left'],

        [SidebarControl, sidebarControlProps, 'top-left'],

        showDrawControl && [DrawControl, drawControlProps, 'top-left'],

        showFullscreenControl && [FullscreenControl, fullscreenControlProps, 'top-left'],

        [LoadingControl, loadingControlProps, 'top-right'],

        [mapboxgl.ScaleControl, {
            unit: user ? user.unitSystem : 'imperial',
            maxWidth: 200
        }, 'bottom-left'],
    ]) as MapboxControl<any>[];
}
